import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Alert, Button, Center, List, Loader, Paper, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../api';
import { StandalonePage } from '../ui/PageScroll';

const MIN_LENGTH = 10;

/**
 * The page behind an invitation or password link: /konto#<token>.
 *
 * Shown whether or not someone is signed in in this browser - the link is
 * for a particular person, not for whoever happens to be logged in.
 *
 * The token is read from the fragment, which the browser never sends to the
 * server, and is then removed from the address bar so it does not linger in
 * the history or get copied along with the URL.
 */
export function AccountPage(): JSX.Element {
  const [token] = useState(() => window.location.hash.replace(/^#/, ''));
  useEffect(() => window.history.replaceState(null, '', '/konto'), []);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');

  const info = useQuery({
    queryKey: ['account-token'],
    queryFn: () => api.inspectToken(token),
    enabled: token.length > 0,
  });
  const redeem = useMutation({ mutationFn: () => api.redeemToken(token, password) });

  const tooShort = [...password].length > 0 && [...password].length < MIN_LENGTH;
  const mismatch = repeat.length > 0 && password !== repeat;
  const ready = [...password].length >= MIN_LENGTH && password === repeat && !redeem.isPending;
  const invite = info.data?.kind === 'invite';

  let body: JSX.Element;
  if (token.length === 0 || info.isError) {
    body = (
      <Stack>
        <Title order={3}>Link nicht gültig</Title>
        <Text size="sm">
          Dieser Link ist ungültig, abgelaufen oder wurde schon benutzt. Bitten Sie die
          Administration um einen neuen.
        </Text>
        <Button variant="default" component="a" href="/">Zur Anmeldung</Button>
      </Stack>
    );
  } else if (info.isPending) {
    body = <Center><Loader /></Center>;
  } else if (redeem.isSuccess) {
    body = (
      <Stack>
        <Title order={3}>{invite ? 'Konto eingerichtet' : 'Passwort gesetzt'}</Title>
        <Text size="sm">
          Melden Sie sich jetzt mit <b>{redeem.data.email}</b> und dem eben gewählten Passwort an.
        </Text>
        <Button component="a" href="/">Zur Anmeldung</Button>
      </Stack>
    );
  } else {
    body = (
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) redeem.mutate();
        }}
      >
        <Stack>
          <Title order={3}>{invite ? 'Willkommen bei PST' : 'Neues Passwort'}</Title>
          <Text size="sm">
            {invite
              ? `Hallo ${info.data.displayName}, wählen Sie ein Passwort für Ihr Konto.`
              : `Wählen Sie ein neues Passwort für ${info.data.displayName}.`}
          </Text>
          {redeem.isError && (
            <Alert color="red">
              {redeem.error instanceof ApiError ? redeem.error.message : 'Das hat nicht geklappt.'}
            </Alert>
          )}
          {/* Lets a password manager store the new password under the right name. */}
          <TextInput label="E-Mail-Adresse" value={info.data.email} readOnly autoComplete="username" />
          <PasswordInput
            label="Passwort"
            autoComplete="new-password"
            required
            data-autofocus
            value={password}
            error={tooShort ? `Mindestens ${MIN_LENGTH} Zeichen` : undefined}
            onChange={(event) => setPassword(event.currentTarget.value)}
          />
          <PasswordInput
            label="Passwort wiederholen"
            autoComplete="new-password"
            required
            value={repeat}
            error={mismatch ? 'Die beiden Eingaben stimmen nicht überein' : undefined}
            onChange={(event) => setRepeat(event.currentTarget.value)}
          />
          <List size="xs" c="dimmed" spacing={2}>
            <List.Item>mindestens {MIN_LENGTH} Zeichen; ein Satz aus mehreren Wörtern ist gut</List.Item>
            <List.Item>nicht die eigene E-Mail-Adresse</List.Item>
          </List>
          <Button type="submit" disabled={!ready} loading={redeem.isPending}>
            {invite ? 'Konto einrichten' : 'Passwort setzen'}
          </Button>
        </Stack>
      </form>
    );
  }

  return (
    <StandalonePage>
      <Paper withBorder shadow="sm" p="xl" w={420} maw="100%">
        {body}
      </Paper>
    </StandalonePage>
  );
}
